import { useId, useRef, useState } from 'react'
import { emit } from '../../platform/analytics/analytics'
import { useScreenViewed } from '../../platform/analytics/useScreenViewed'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Button } from '../../platform/primitives/Button'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { AlertPanel, Panel } from '../../platform/primitives/Panel'
import { SectionHeader } from '../../platform/primitives/ListRow'
import { useAuth } from '../../auth/useAuth'
import { usePageMeta } from '../../shell/PageMeta'
import { InvitationRow } from './InvitationRow'
import { InviteDialog } from './InviteDialog'
import { MemberRow } from './MemberRow'
import {
  canManageMembers,
  leaveBlockReason,
  memberLabel,
  ROLE_LABELS,
  ROLE_PENDING_LEAVE_REASON,
  ROLE_UNCONFIRMED_LEAVE_REASON,
} from './roles'
import { useHousehold, useHouseholdId } from './useHousehold'
import { useInvitations, useResendInvitation, useRevokeInvitation } from './useInvitations'
import { myRoleOf, useChangeRole, useLeaveHousehold, useMembers, useRemoveMember } from './useMembers'
import './household.css'

/**
 * `/household` — M12-R1: every active member with avatar, name, email, role
 * and joined date. Replaces the `<Placeholder name="Members" />` the nav
 * destination (`src/shell/nav.ts`, labelled "Members") has rendered until now.
 *
 * **Task 4 rendered only.** No role menu, no remove, no leave, no invite.
 * **Task 5 mounted the first**: `members`, `myUserId`, `myRole` and `change`
 * (from `useChangeRole`) are passed to every row here rather than left for a
 * later task to wire — a control `MemberRow`/`RoleMenu` can fully render and
 * test but this screen never mounts with real props is a defect this repo
 * has shipped once already (five clean sweeps over an unreachable control).
 * **Task 6 (below) mounts remove the same way**: `onRemove` (a wrapper around
 * `useRemoveMember`'s own `remove` — issue 366's toast, see `toast`'s own
 * declaration below for why) goes to every row alongside the props Task 5
 * already wired — `MemberRow`'s own `MemberActionsMenu` decides, per row,
 * whether that resolves to a Remove item, a Leave item, or neither. Task 7
 * adds the
 * footer "Leave household" control the same way. **Task 9 adds invite**:
 * `InviteDialog`, reachable from two places that are deliberately the same
 * action rather than two — the button beside the invitations heading, and
 * the empty state's own offer (M12-R7). Both set one piece of state, so
 * there is one dialog and it cannot be opened twice.
 *
 * **Three terminal states `useHouseholdId` can resolve to INSTEAD of an id**,
 * each rendered before the member list is ever reached. `useMembers(undefined)`
 * needs no special casing for the fourth and fifth possibilities (`pending`,
 * `resolved`) — it stays `enabled: false` and reports `'loading'` through
 * `fromQuery`, which is the honest state while resolution is still in flight
 * or has succeeded and the members request just hasn't answered yet.
 *
 * - **`isForbidden`.** Found at the Task 2 gate, filed as issue 321: every
 *   `GET /households/{id}` call this app made while resolving 403'd. The
 *   client cannot confirm WHY — role comes only from `/members`, which is the
 *   endpoint refusing the request, and `GET /households` carries no role at
 *   all — so the copy states what IS true and stops there. It deliberately
 *   never says "viewer": a removed member reaches this identical 403.
 * - **No household at all** (`id === undefined`, and nothing pending or
 *   forbidden). `dependencies.py:288` swallows a household-creation failure
 *   at signup — `"Don't fail user creation if household creation fails"` — so
 *   this is reachable, not hypothetical. Left unrendered the screen would sit
 *   on a permanent loading skeleton: `enabled: false` never lets `fromQuery`
 *   past `'loading'`. **No offer to create one** — `/household/new` is not
 *   built (M12-R15 stays dropped), and a user who lands here has a broken
 *   account, not a household to name.
 * - **`isError`.** Two failures collapse onto this flag, and `errorCause`
 *   tells them apart (issue 311, blocking finding 3) because their correct
 *   copy differs:
 *   - **`'list'`** — the household LIST request itself failed (a 500, say),
 *     the same permanent-skeleton failure as the no-household case, for a
 *     different reason. `useHouseholdId` exposes no `refetch` for that query,
 *     so this is the one place in the module that reaches for
 *     `window.location.reload()` — the same fallback `Offline.tsx` and
 *     `ErrorBoundary.tsx` already use when no narrower retry exists. A list
 *     failure genuinely may be transient, so the reload offer is honest here.
 *   - **`'detail'`** — every household's detail call failed, but not
 *     uniformly 403 (that's `isForbidden` above) — some other status, or a
 *     mix. `isRetryableHouseholdError` already refused to retry a 4xx, so a
 *     bare reload cannot fix what a reload-worth of retries already couldn't.
 *     Offering it anyway is a recovery that never recovers, aimed at the same
 *     population `isForbidden` exists for — a viewer whose OTHER memberships
 *     also 403'd alongside the one that made it uniform enough to name. This
 *     branch renders the identical not-available copy as `isForbidden`,
 *     deliberately: the client cannot tell a viewer's 403 from a 500 it
 *     cannot narrate uniformly, and must not claim to.
 *
 * **Task 8 adds a second, independent panel: pending invitations.** Its own
 * `<Async surface="household-invitations">`, not a second branch inside the
 * members one — `useMembers` and `useInvitations` are two separate requests
 * that fail independently, and one `<Async>` cannot speak for both. Unlike
 * the member list, EMPTY is real and common here (`household-invitations`'s
 * own docblock) — a household with nobody invited is the normal state, not
 * an edge case to special-case away.
 *
 * `onResend`/`onRevoke` are not passed at all here — both are optional on
 * `InvitationRow`, and omitting them renders no actions rather than two
 * buttons wired to `() => {}`. A live-looking control that silently does
 * nothing when clicked is worse than an absent one (issue 315 review,
 * finding N3): the mutations behind those props —
 * `useResendInvitation`/`useRevokeInvitation` — are Task 10's, the same way
 * Task 4 shipped `MemberRow` with no actions before Tasks 5–7 added them.
 *
 * **The panel is gated on `canManageMembers(myRole)`, and the gate reaches
 * the QUERY, not only the render.** `GET /invitations` is ADMIN-or-higher
 * (`openapi.json`'s own description) — a member or viewer 403s. `roles.ts`'s
 * own docblock already lists "list invitations" among the five actions
 * `canManageMembers` covers, so this reuses it rather than inventing a
 * narrower predicate. `useInvitations` is passed `undefined` rather than the
 * real id whenever the gate is closed, the same way `useMembers(undefined)`
 * already works while `householdId` itself is unresolved — so a role that
 * cannot see this list never causes the request that would 403 to fire at
 * all, and the panel never has an unretryable error to render.
 *
 * `myRole` comes from `myRoleOf(members, myUserId)`, which is `null` both
 * while the members list or the signed-in identity is not yet known AND for
 * a role outside the enum (`useMembers.ts`'s own docblock) — and
 * `canManageMembers(null)` is `false`. So "not yet known" and "an
 * unrecognised role" both fail CLOSED, exactly like "denied": the panel
 * starts absent and appears once the gate genuinely opens, rather than
 * flashing in and then being taken away, and a future fifth role this client
 * has not been taught is never treated as capable by default.
 */
export function Household() {
  const householdId = useHouseholdId()
  const householdState = useHousehold(householdId.id)
  const membersState = useMembers(householdId.id)
  const auth = useAuth()
  const myUserId = auth.status === 'signedIn' ? auth.profile.id : undefined
  // Not named `members` — the members `<Async>` render prop below binds that
  // name to its own resolved list, and shadowing it here would read as the
  // same list when it is only defined in the one status this reads.
  const myRole = myRoleOf(
    membersState.status === 'success' ? membersState.data : undefined,
    myUserId,
  )
  const canSeeInvitations = canManageMembers(myRole)
  const invitationsState = useInvitations(canSeeInvitations ? householdId.id : undefined)
  // `pending_invite_count` depends on ORDER: the invitations query is only
  // enabled once members have loaded (`myRole` above), so for owners and
  // admins `household_viewed` waits (`ready`) until that query has settled —
  // success, empty, stale or error — then fires once (invitations never
  // produce stale today: the response has no sync date). Count on success, 0 on empty,
  // omitted on error (the event still fires). A role that cannot see the
  // panel never waits and never carries the field. Issue 623. What "viewed"
  // means: `useScreenViewed`'s docblock.
  const invitationsSettled =
    invitationsState.status === 'success' ||
    invitationsState.status === 'empty' ||
    invitationsState.status === 'stale' ||
    invitationsState.status === 'error'
  useScreenViewed(membersState, (members) =>
    emit('household_viewed', {
      member_count: members.length,
      pending_invite_count:
        invitationsState.status === 'success' || invitationsState.status === 'stale'
          ? invitationsState.data.length
          : invitationsState.status === 'empty'
            ? 0
            : undefined,
    }),
    !canSeeInvitations || invitationsSettled,
  )
  const [inviting, setInviting] = useState(false)
  const [leaving, setLeaving] = useState(false)
  /**
   * The viewer's OWN row, and why leaving is or isn't allowed right now —
   * Task 7, tracke5-webapp#314. `leaveBlockReason` is the same function
   * `MemberActionsMenu`'s own Leave item calls on the identical
   * `rawRole`/`members` values, so the footer control below and the row item
   * can never disagree about whether — or why — leaving is blocked.
   */
  const myMember =
    membersState.status === 'success'
      ? membersState.data.find((m) => m.userId === myUserId)
      : undefined
  /**
   * `leaveReason` is the TEXT to show, and `leaveBlocked` the fact of being
   * blocked. The loading window used to be the one case where those came
   * apart (web-architect review, issue 314 fix round, B1) — **it no longer
   * does, and closing that gap is the point.** Every arm below produces a
   * reason now, so the two are the same fact and `leaveBlocked` says so
   * directly. See its own note for what the divergence cost.
   *
   * `useMembers` is `enabled: householdId !== undefined`
   * (`useMembers.ts`), so `membersState` cannot be `success` before the id
   * resolves — but the footer below is gated on the id alone (it must be:
   * see `leaveButton`'s own docblock for why it stays mounted regardless of
   * member-list status). So there IS a genuine loading window on every load
   * of `/household`, and it must fail closed (`leaveBlocked = true`)
   * WITHOUT wearing the copy of an actual failure — a request that simply
   * hasn't answered yet is not the same fact as a role this client could
   * not confirm. That is what `ROLE_PENDING_LEAVE_REASON` is for: its own
   * sentence, saying the thing that is actually true of a pending request
   * (it resolves on its own), rather than either borrowing the error's copy
   * or — as this arm did until issue 359's verify run caught it — saying
   * nothing at all and leaving the control blocked but unexplained.
   * `error` reuses `ROLE_UNCONFIRMED_LEAVE_REASON` because it
   * genuinely IS that fact: the request failed, so there is nothing further
   * to wait for and no more honest answer than the one an unclassifiable
   * role already gets. `success` is the only status `leaveBlockReason`
   * itself is asked to judge, on `myMember?.rawRole` — `undefined` there
   * covers both "identity not yet known" and "the viewer's own row is
   * (structurally impossible today) absent from the list", the same
   * fail-closed treatment an unclassified role gets.
   *
   * **`error`'s honesty rests on a policy decided in `fromQuery.ts:41-50`,
   * not here** (web-architect, N10). `error` is reported only when
   * `data === undefined` — a BACKGROUND refetch failure keeps `success` and
   * keeps the list on screen. So `error` means the member list has never
   * loaded and the viewer's own row has never been seen, which is what makes
   * "your role could not be confirmed" literally true rather than merely
   * convenient. If `fromQuery` ever reports `error` while data is present,
   * this copy starts lying to a user who can see their own row — change it
   * here in the same edit.
   */
  const leaveReason =
    membersState.status === 'success'
      ? leaveBlockReason(myMember?.rawRole, membersState.data)
      : membersState.status === 'error'
        ? ROLE_UNCONFIRMED_LEAVE_REASON
        : ROLE_PENDING_LEAVE_REASON
  /**
   * **`leaveReason !== null` alone, now — the two can no longer come apart.**
   *
   * This used to be `membersState.status !== 'success' || leaveReason !== null`
   * because the loading window was blocked with no reason to show. That gap
   * was the defect: a blocked control with nothing naming why is what rule 11
   * and `DisabledReason.tsx` exist to prevent, and this footer wore it on
   * every load of `/household`.
   *
   * Found by `household-blocked-control.spec.ts:203` failing intermittently
   * in `make verify` — it asserts a blocked control names its reason, and it
   * won or lost that race on how fast the members query settled. Re-running
   * cleared it, which is exactly how this would have stayed hidden. The spec
   * was right; the component was wrong.
   *
   * The loading window now has its OWN text rather than borrowing the error's
   * (see `ROLE_PENDING_LEAVE_REASON`), which was the trap the previous fix
   * round correctly avoided and then over-corrected past. With every arm
   * producing a reason, "blocked" and "has a reason" are the same fact, and
   * this expression says so rather than restating the statuses.
   */
  const leaveBlocked = leaveReason !== null
  const leaveReasonId = useId()
  /**
   * Where `InviteDialog` puts focus back, and why it is THIS button.
   *
   * The dialog has two openers: this one, and the empty state's chip below.
   * The chip is the one that cannot be restored to — a successful send makes
   * the invitation list non-empty, which unmounts the empty state and the chip
   * with it. This button is rendered by the same branch as the panel itself,
   * so it survives every path the dialog can close on.
   */
  const inviteButton = useRef<HTMLButtonElement>(null)
  /**
   * Where the Leave confirmation restores focus — and why THIS button,
   * rather than `inviteButton` (Task 7, tracke5-webapp#314).
   *
   * `inviteButton` only mounts under `canSeeInvitations`, but leaving needs
   * no permission at all — every member may leave their own household — so a
   * plain member opening the row's own Leave item would have no anchor if
   * this reused that one. This button, unlike `inviteButton`, unlike every
   * `MemberRow`, and unlike the invitations panel, is not conditioned on role
   * or on any per-row data: it renders whenever `householdId.id` does, so it
   * survives BOTH close paths — the footer's own click (where it was never
   * going anywhere) and the row item's click (where the row itself is
   * destroyed the moment the viewer actually leaves, since the member list
   * that follows belongs to a different household entirely).
   */
  const leaveButton = useRef<HTMLButtonElement>(null)
  /**
   * All five mutations live HERE, not in the row, and the reason that holds
   * without qualification is arity: a row renders N times and `InviteDialog`
   * is a singleton. A hook per row would open N subscriptions to one query
   * and give each row its own `isPending`, which is not the same fact as
   * "this row is sending" — the row (`InvitationRow`'s `resending`,
   * `RoleMenu`'s `changing`, and now `MemberActionsMenu`'s own
   * `useRowMutation`) keeps its own flag for that; `useRowMutation.ts`'s
   * docblock states the arity argument once so three call sites don't repeat
   * it. `leave` has no per-row flag to duplicate — the dialog it drives is
   * itself a Household-level singleton, exactly like `InviteDialog`.
   *
   * "Presentational components do not fetch" points the same way, but on its
   * own it would have to explain why `InviteDialog` owns its write, and the
   * honest answer there is that the dialog's write had no other home.
   *
   * `householdId.id ?? ''` is safe at this point and not a shrug, for all
   * five: no row exists, at any role, until the id resolves, and the footer
   * control below is itself gated on `householdId.id !== undefined`.
   * `MemberRow` renders only inside `<Async>` on `membersState.status ===
   * 'success'`, and `useMembers` is `enabled: householdId !== undefined`
   * (`useMembers.ts`) — so `resend`/`revoke`/`change`/`remove` can never
   * actually be called with the fallback in play, and `leave` cannot either,
   * since its own trigger is absent under the same condition. The fallback
   * itself exists only because the hooks take a `string` and Rules of Hooks
   * put these calls before any of the narrowing below (web-architect review,
   * issue 312, finding N5 — corrected from an earlier version of this
   * comment that argued the point via `canSeeInvitations`, which is true of
   * `resend`/`revoke` but not of the simpler fact above).
   */
  const { resend } = useResendInvitation(householdId.id ?? '')
  const { revoke } = useRevokeInvitation(householdId.id ?? '')
  const { change } = useChangeRole(householdId.id ?? '')
  const { remove } = useRemoveMember(householdId.id ?? '')
  const { leave } = useLeaveHousehold(householdId.id ?? '')
  /**
   * Issue 366 — a confirmed remove and a confirmed revoke used to succeed
   * SILENTLY: the dialog closed, the row vanished, and nothing announced
   * that anything had happened, which is no confirmation at all for a
   * screen-reader user. `useToast()` throws outside a `<ToastProvider>`
   * (`ToastProvider.tsx`'s own guard) — safe to call unconditionally here
   * ONLY because `main.tsx` mounts one above `AppRoutes`, so every real
   * render of `/household` already has one. `Categories` does the same for
   * a confirmed category delete (#376): the route owns the confirm and the
   * toast, and the presentational `CategoryTable` — rendered bare by
   * `Probe.tsx` — stays provider-free. "The row vanished" announces nothing
   * to someone who cannot see it vanish, which is what #366 and #376 rebut.
   *
   * **The toast lives here, not inside `MemberActionsMenu`/`InvitationRow`.**
   * Same reasoning as the five mutations two paragraphs above: those two
   * components stay presentational, and giving either its own `useToast()`
   * would be a second, row-scoped provider dependency for a queue that is
   * meant to be one per app. `onRemove`/`onRevoke` below are thin wrappers
   * around `remove`/`revoke` that raise the toast AFTER the request settles
   * (an early toast for a request that later fails would be a confirmation
   * of something that didn't happen) — `MemberActionsMenu`/`InvitationRow`
   * still only see "a promise that resolves or rejects", exactly as before.
   *
   * **Why the toast is announced at all, given it arrives while
   * `ConfirmDialog` is still open** (the `await` above resolves inside
   * `onConfirm`, before the dialog's own `open` prop flips): it escapes
   * `Overlay`'s `inert` on the rest of the page ONLY because the toaster's
   * host carries `data-overlay-passthrough` (`ToastProvider.tsx`). Rename or
   * drop that attribute and both toasts go silently un-announced.
   *
   * **The copy says only what `remove_member` actually does.** `useMembers.
   * ts`'s own `useRemoveMember` docblock has the full trace: removing does
   * NOT run `leave_household`'s account-sharing cleanup, and the confirmation
   * dialog's "will lose access to everything shared here" clause was cut for
   * exactly that reason (web-architect review, issue 313, blocking finding
   * B2; the gap is filed as `expense-monitor-backend#314`). A toast
   * reintroducing that claim in fewer words would still be reintroducing it —
   * so this says only "Removed {label}", the fact the client can actually
   * stand behind, not a security property the system does not provide.
   *
   * **Focus does NOT move to the toast.** `ToastProvider.tsx`'s own docblock
   * is explicit that an arriving toast must not steal focus — it is announced
   * through the live region, and reached on purpose via the F6 landmark route
   * (`useRegionRotor`, issue 216), never automatically. So `inviteButton`
   * (below) stays the REAL destination `ConfirmDialog`'s `restoreFocusTo`
   * sends focus to after a confirmed remove — not a fallback standing in for
   * a toast that never claims focus — and `MemberActionsMenu`'s own
   * `restoreFocusTo` docblock needed no change for #366: what it already
   * says about `inviteButton` surviving the row's removal remains exactly
   * true with a toast now also on screen.
   *
   * **That is measured true for remove, and measured FALSE for revoke
   * today.** `document.activeElement` lands on `<body>` after a confirmed
   * revoke, not on `inviteButton`, and F6 needs two presses to reach the
   * toast — the first lands on `<main>`. The cause is pre-existing and not
   * this diff's: #355 — `Overlay` restores focus on CLOSE, not on UNMOUNT,
   * and revoking unmounts both the dialog and the row that held its trigger
   * before that restore lands, so there is nothing left for it to land on.
   * `invitation-actions.spec.ts` already `test.fixme`'s the identical gap for
   * the row's own action. This goes right with no edit here once #355 lands.
   *
   * **Resend gets no toast.** `InvitationRow`'s own `resent` branch already
   * renders `role="status"` — "Sent again. The previous link no longer
   * works — share this one instead." — inline, on the row, the moment a
   * resend succeeds (`InvitationRow.tsx`). That is already an announced,
   * visible confirmation naming the actual consequence; a toast repeating
   * "Resent" beside it would be an echo, not new information, so `onResend`
   * below stays the bare `resend` from `useResendInvitation`, unwrapped.
   */
  const toast = useToast()

  /* The chrome's top bar carries the count; the on-canvas heading below
     carries the household's own NAME, which the breadcrumb has no way to
     know — the same split Merchants/Categories already draw between the two.
     `null` while there is nothing to say yet, so the bar shows no line rather
     than a stale or wrong one.

     Counted from `membersState.data.length`, not `householdState.data.
     member_count` — both are ACTIVE-only server-side so they agree in
     principle, but the members list is already in hand here and costs no
     extra request, where the household detail is a second round trip this
     screen would otherwise wait on just to say a number the list already
     knows (web-architect review, issue 311, smaller finding 3). */
  usePageMeta(
    membersState.status === 'success'
      ? `${membersState.data.length} member${membersState.data.length === 1 ? '' : 's'}`
      : null,
  )

  /* `isForbidden` (a uniform 403 across every household) and `errorCause ===
     'detail'` (a non-uniform detail failure) get the SAME copy — the client
     cannot distinguish a viewer's 403 from a 500 it cannot narrate uniformly,
     and must not claim to be more certain than that. Never "viewer": a
     removed member reaches an identical shape. */
  if (householdId.isForbidden || householdId.errorCause === 'detail') {
    return (
      <AlertPanel style={{ gap: 9 }}>
        <span role="alert" style={{ fontSize: 'var(--type-row-strong)' }}>
          This household is not available to you.
        </span>
        <span>
          Your access may have changed, or your role may not include viewing members. Ask a
          household admin to check.
        </span>
      </AlertPanel>
    )
  }

  if (!householdId.isPending && !householdId.isError && householdId.id === undefined) {
    return (
      <AlertPanel style={{ gap: 9 }}>
        <span role="alert" style={{ fontSize: 'var(--type-row-strong)' }}>
          Your account has no household.
        </span>
        <span>
          Something went wrong when your account was set up. Sign out and back in, or contact
          support if this continues.
        </span>
      </AlertPanel>
    )
  }

  if (householdId.errorCause === 'list') {
    return (
      <AlertPanel style={{ gap: 9 }}>
        <span role="alert" style={{ fontSize: 'var(--type-row-strong)' }}>
          Household could not be loaded.
        </span>
        <span>
          <Button size="panel" variant="outline" onClick={() => window.location.reload()}>
            Reload the page
          </Button>
        </span>
      </AlertPanel>
    )
  }

  return (
    <>
      {/* `householdState`'s `'loading'` and `'error'` were unrendered here —
          the heading and the chrome's count silently vanished rather than
          degrading, and `useHousehold`'s own `recovery: { label: 'Try
          again' }` (asserted by `useHousehold.test.tsx`) was unreachable in
          production (web-architect review, issue 311, smaller finding 4).
          Not routed through `<Async>`: this screen already has one, for the
          member list, and `useHousehold.ts`'s own docblock is deliberate
          about not stacking a second — two skeletons for one screen. These
          two branches are the minimal alternative: they degrade the ONE
          thing this query backs (the name) rather than the whole screen. */}
      {householdState.status === 'loading' && (
        <div className="household__header">
          <Skeleton height={28} width={200} />
        </div>
      )}
      {householdState.status === 'error' && (
        <div className="household__header household__header--degraded">
          <h2 className="household__title household__title--degraded">
            Household name unavailable.
          </h2>
          {householdState.recovery && (
            <Button size="panel" variant="outline" onClick={householdState.recovery.onAct}>
              {householdState.recovery.label}
            </Button>
          )}
        </div>
      )}
      {householdState.status === 'success' && (
        <div className="household__header">
          <h2 className="household__title">{householdState.data.name}</h2>
        </div>
      )}
      <Panel>
        <Async
          surface="household-members"
          state={membersState}
          skeleton={
            <>
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
            </>
          }
          // Unreachable — `useMembers` passes no `isEmpty` to `fromQuery`; the
          // signed-in user reading this screen is necessarily one of the
          // rows. Required anyway: `Async`'s own `empty` prop is mandatory so
          // the state cannot be forgotten by a future caller that does filter.
          empty={{ title: 'No members yet.' }}
          announce={(members) => `${members.length} member${members.length === 1 ? '' : 's'}`}
        >
          {(members) => (
            <>
              {members.map((m) => (
                <MemberRow
                  key={m.id}
                  member={m}
                  members={members}
                  myUserId={myUserId}
                  myRole={myRole}
                  // A wrapper, for the same reason `onRemove` below is one —
                  // issue 377, the third mutation on this screen and the one
                  // #366 did not cover. Raised AFTER `change` settles, so a
                  // request that later fails never toasts a success.
                  //
                  // **The card asked whether this was needed at all, since a
                  // role change is reversible and leaves its trigger in
                  // place. Measured in a browser, and it is.** Choosing an
                  // option:
                  //   1. unmounts the menu — so #369's `aria-checked`, the
                  //      obvious candidate, is destroyed at the very moment
                  //      the state changes and can announce nothing;
                  //   2. moves focus back to the trigger while its
                  //      description still reads "Currently {OLD role}" —
                  //      confirmed unchanged at 0, 50, 200, 600 and 1400ms
                  //      after the click, so the one announcement a screen
                  //      reader does get states the role the member no
                  //      longer holds;
                  //   3. updates that description silently once the refetch
                  //      lands, on an element that already has focus, with
                  //      no live region anywhere on the page gaining text.
                  //
                  // (3) is precisely the case the card flagged as not
                  // reliably announced by assistive technology. So the
                  // outcome needs a channel of its own.
                  onChangeRole={async (membershipId, next) => {
                    await change(membershipId, next)
                    toast.show({
                      kind: 'success',
                      message: `${memberLabel(m)} is now ${ROLE_LABELS[next]}`,
                    })
                  }}
                  // A wrapper around `useRemoveMember`'s own `remove`, not
                  // `remove` itself — issue 366. Raised AFTER `remove`
                  // settles, so a request that later fails never gets a
                  // toast claiming it succeeded; `MemberActionsMenu` sees
                  // only a promise either way and does not know the
                  // difference. `memberLabel(m)` is captured from THIS row's
                  // own member, not re-looked-up after the list refetches —
                  // the row that is about to be removed is exactly the row
                  // this closure already has in hand.
                  onRemove={async (membershipId) => {
                    await remove(membershipId)
                    toast.show({ kind: 'success', message: `Removed ${memberLabel(m)}` })
                  }}
                  // The SAME handler the footer control below uses — the
                  // row's own Leave item and the footer open the identical
                  // confirmation, not two (Task 7, tracke5-webapp#314).
                  // `MemberActionsMenu` itself never calls this unless
                  // `isSelf`, so it is harmless to pass on every row.
                  onLeave={() => setLeaving(true)}
                  // The same anchor `InvitationRow`'s revoke already uses
                  // (`:371,394` below) — it survives a confirmed removal,
                  // where the row's own trigger does not (web-architect
                  // review, issue 313, blocking finding B1). Guaranteed
                  // mounted whenever Remove itself can be offered: Remove
                  // requires `!isSelf`, so `canOpenActions` here reduces to
                  // `canManageMembers(myRole)` — the exact condition that
                  // also mounts the invitations panel and this button
                  // (`canSeeInvitations`, above).
                  restoreFocusTo={inviteButton}
                />
              ))}
            </>
          )}
        </Async>
      </Panel>
      {/* Absent entirely for a role that cannot list invitations — never
          rendered in an error state. See this component's own docblock for
          why the gate reaches the query too, and why "not yet known" fails
          the same way "denied" does. */}
      {canSeeInvitations && householdId.id !== undefined && (
        <>
          <div className="household__section-header">
            <SectionHeader title="Pending invitations" />
            {/* The page body's one lime fill. `--accent` may fill the primary
                action per content area (M0-R9), and inviting someone is what
                this screen is for; every other control here — Resend, Revoke,
                the degraded header's Try again — is an `outline`. The empty
                state's own offer below is a `chip` (`Async` renders
                `empty.action` at the default variant), so the two never put
                two lime fills on one screen. */}
            <Button
              ref={inviteButton}
              size="panel"
              variant="primary"
              onClick={() => setInviting(true)}
            >
              Invite someone
            </Button>
          </div>
          <Panel>
            <Async
              surface="household-invitations"
              state={invitationsState}
              skeleton={
                <>
                  <Skeleton height={44} />
                  <Skeleton height={44} />
                </>
              }
              // M12-R7, and the spec's own States table: "Empty → explain what
              // an invitation does, offer to send one." Task 9 supplies the
              // offer the Task 8 note here said was still missing.
              //
              // Title and body are separate (issue 315 finding N2) — the
              // original single sentence ran to 160 characters rendered at
              // heading weight; a real title plus its own body text reads as
              // a page section, not a run-on.
              empty={{
                title: 'No pending invitations',
                body:
                  'Inviting someone gives them access to this household — their own accounts ' +
                  'and transactions stay theirs.',
                action: { label: 'Invite someone', onAct: () => setInviting(true) },
              }}
            >
              {(invitations) => (
                <>
                  {invitations.map((invitation) => (
                    <InvitationRow
                      key={invitation.id}
                      invitation={invitation}
                      // Bare `resend`, deliberately not wrapped — see the
                      // docblock above `toast`'s own declaration for why
                      // resend gets no toast (`InvitationRow`'s own inline
                      // `role="status"` already announces it).
                      onResend={resend}
                      // A wrapper around `useRevokeInvitation`'s own `revoke`,
                      // matching `onRemove` above and for the identical
                      // reason (issue 366) — raised only once `revoke`
                      // settles, and `invitation.email` is this row's own,
                      // not re-read after the list refetches.
                      onRevoke={async (id) => {
                        await revoke(id)
                        toast.show({
                          kind: 'success',
                          message: `Revoked invitation to ${invitation.email}`,
                        })
                      }}
                      // The section header's Invite button, which outlives any
                      // row: revoking removes the row that opened the dialog,
                      // so its own control cannot be the place focus returns.
                      restoreFocusTo={inviteButton}
                    />
                  ))}
                </>
              )}
            </Async>
          </Panel>
          {/* Mounted only while open, and handed the two lists this screen has
              already loaded — `InviteDialog` fetches neither. A presentational
              component does not fetch (CLAUDE.md), and both queries are in hand
              here; refetching them inside the dialog would be a second opinion
              about the same two lists, which is exactly the drift
              `inviteBlockedReason` exists to avoid.

              An empty array when either query has not resolved: the dialog's
              duplicate check can only ever report FEWER blocks than it should
              in that window, and the server's own two guards are still behind
              it — `InviteDialog`'s 400 arm puts that answer on the same field
              the client-side check would have. The reverse (blocking on a list
              we do not have) would refuse a legitimate invitation with a
              reason that is not true. */}
          {inviting && (
            <InviteDialog
              restoreFocusTo={inviteButton}
              open
              householdId={householdId.id}
              members={membersState.status === 'success' ? membersState.data : []}
              invitations={invitationsState.status === 'success' ? invitationsState.data : []}
              onClose={() => setInviting(false)}
            />
          )}
        </>
      )}
      {/* The footer control — Task 7, tracke5-webapp#314. No `canManageMembers`
          gate: every member, at every role, may leave their own household
          (`household_service.py` has no permission check on `leave_household`
          at all, unlike remove or a role change), so this is conditioned only
          on the id existing, matching `inviteButton`'s own "nothing to act on
          yet" reasoning rather than its role gate. */}
      {householdId.id !== undefined && (
        <div className="household__footer">
          <Button
            ref={leaveButton}
            size="panel"
            variant="outline"
            // Same `aria-disabled` reasoning as `MemberActionsMenu`'s items —
            // a native `disabled` control drops out of the tab order, and the
            // stated reason next to it would then never reach a keyboard or
            // screen-reader user.
            //
            // `leaveBlocked` and `leaveReason !== null` are now the SAME
            // expression (see `leaveBlocked`'s own note) — they used to
            // differ, and this comment used to explain why. The divergence
            // was the defect: it left this control blocked with no reason
            // during the members query, on every load. Both attributes are
            // kept spelled out rather than collapsed, because they answer two
            // different questions and a future arm could reasonably make them
            // diverge again — deliberately, and with a reason.
            aria-disabled={leaveBlocked || undefined}
            aria-describedby={leaveReason !== null ? leaveReasonId : undefined}
            onClick={() => {
              // `aria-disabled` still dispatches a click — the guard native
              // `disabled` would otherwise give for free.
              if (leaveBlocked) return
              setLeaving(true)
            }}
          >
            Leave household
          </Button>
          {leaveReason !== null && (
            <p id={leaveReasonId} className="household__leave-reason">
              {leaveReason}
            </p>
          )}
        </div>
      )}
      {/**
       * One dialog, not one per entry point. Opened by the footer button
       * above AND by `MemberActionsMenu`'s Leave item on the viewer's own
       * row (`onLeave={() => setLeaving(true)}` on every `MemberRow` above) —
       * see `leaveButton`'s own docblock for why THIS anchor, rather than
       * `inviteButton`, is the one that survives both.
       *
       * M12-R12 as restated at intake: the original spec said leaving the
       * only household "routes to setup"; `leave_household` actually creates
       * a replacement personal household and returns the viewer to it, so
       * the confirmation says where they land rather than repeating that
       * wrong claim.
       */}
      <ConfirmDialog
        open={leaving}
        mode="leave_household"
        title="Leave household?"
        body={
          `You'll return to your own household. ` +
          `${householdState.status === 'success' ? householdState.data.name : 'This household'} ` +
          'and its shared accounts will no longer be visible to you.'
        }
        confirmLabel="Leave household"
        destructive
        restoreFocusTo={leaveButton}
        onCancel={() => setLeaving(false)}
        onConfirm={async () => {
          // Task 12. Taken from the roster BEFORE `leave()` — `onSettled`
          // invalidates everything (`useLeaveHousehold`'s own docblock), so
          // by the time this resolves `membersState` may already be
          // re-fetching a DIFFERENT household's roster entirely. Carried
          // under the already-registered `member_count` (fix round 2, issue
          // 319 blocking finding B1) — the "before" is the event NAME,
          // `household_left`, so a second key restating it would be a
          // redundant slot with its own chance to be misspelled.
          const memberCountBefore =
            membersState.status === 'success' ? membersState.data.length : undefined
          await leave()
          emit('household_left', { member_count: memberCountBefore })
          setLeaving(false)
        }}
      />
    </>
  )
}
